/**
 * TenantSwitcher — owner multi-tenant top-bar switcher (Prompt 11E.5 / D9).
 *
 * Only rendered for `owner` accounts with membership in >1 tenant
 * (`useTenant().isSwitcherVisible`) — every other role/account never sees
 * this element, per the acceptance criteria ("non-owner never sees the
 * switcher").
 *
 * Selecting a real tenant sets it as the default `X-Tenant-Id` for every
 * subsequent API call (see `@core/api/client`). Selecting "All" clears the
 * default override — "All" is a read-only, frontend-only concept; the
 * Entity Framework's `useTenantAwareEntityList` fans out one request per
 * tenant with an explicit per-call header when in this mode. There is no
 * server-side "all tenants" value.
 *
 * Tags: candeza-tenant-switcher, candeza-tenant-option-{id}, candeza-tenant-all.
 */

import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTenant, ALL_TENANTS } from '@core/tenant/TenantContext';

const Wrapper = styled.div`
  position: relative;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  max-width: 200px;
  white-space: nowrap;
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 180px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: 4px;
  z-index: ${({ theme }) => theme.zIndex.dropdown};
`;

const MenuItem = styled.button<{ $selected?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: 6px 10px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ $selected, theme }) => ($selected ? theme.colors.primaryLight : 'transparent')};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
  }
`;

export const TenantSwitcher: React.FC = () => {
  const { isSwitcherVisible, availableTenantIds, selectedTenantId, setSelectedTenantId } =
    useTenant();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!isSwitcherVisible) return null;

  const label = selectedTenantId === ALL_TENANTS ? 'All schools' : selectedTenantId;

  const select = (id: string) => {
    setSelectedTenantId(id);
    setOpen(false);
  };

  return (
    <Wrapper ref={ref} data-testid="candeza-tenant-switcher">
      <Trigger
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Switch school"
      >
        🏫 {label}
      </Trigger>
      {open && (
        <Menu role="menu">
          {availableTenantIds.map((id) => (
            <MenuItem
              key={id}
              type="button"
              role="menuitem"
              $selected={id === selectedTenantId}
              onClick={() => select(id)}
              data-testid={`candeza-tenant-option-${id}`}
            >
              {id}
            </MenuItem>
          ))}
          <MenuItem
            type="button"
            role="menuitem"
            $selected={selectedTenantId === ALL_TENANTS}
            onClick={() => select(ALL_TENANTS)}
            data-testid="candeza-tenant-all"
          >
            All schools
          </MenuItem>
        </Menu>
      )}
    </Wrapper>
  );
};

export default TenantSwitcher;



